import { Translation } from '../../../models/translation';
import { TranslateService } from '@ngx-translate/core';
import { InventoryViewMode } from '../../../services/base-actions/items-inventory.service';

export interface FilterField {
  name: InventoryFilters;
  label: string;
  type: 'text' | 'select' | 'autocomplete' | 'date';
}

// Short labels for filter inputs (tooltips show full names)
export const filtersTranslations: Record<string, Translation> = {
  lotItemNumbers: {
    es: 'Lote #',
    en: 'Lot #',
  },
  itemCodes: {
    es: 'Artículo',
    en: 'Item',
  },
  providerReferences: {
    es: 'Proveedor',
    en: 'Provider',
  },
  manufacturerReferences: {
    es: 'Fabricante',
    en: 'Mfg',
  },
  manufacturerLotNumbers: {
    es: 'Lote Fab.',
    en: 'Mfg Lot #',
  },
  manufacturerLabelNumbers: {
    es: 'Etiq. Fab.',
    en: 'Mfg Label #',
  },
  invoiceReferences: {
    es: 'Factura',
    en: 'Invoice',
  },
  deliveryNoteReferences: {
    es: 'Nota Entrega',
    en: 'Delivery Note',
  },
  fromDateReceived: {
    es: 'Fecha de Recepción Desde',
    en: 'From Date Received',
  },
  toDateReceived: {
    es: 'Fecha de Recepción Hasta',
    en: 'To Date Received',
  },
  excludeZeroBalances: {
    es: 'Excluir saldos cero',
    en: 'Exclude zero balances',
  },
  excludeSplitLotItems: {
    es: 'Excluir lotes divididos',
    en: 'Exclude split lot items',
  },
  locations: {
    es: 'Ubicaciones',
    en: 'Locations',
  },
  locationsTooltip: {
    es: 'Seleccione una o más ubicaciones del árbol jerárquico',
    en: 'Select one or more locations from the hierarchical tree',
  },
};

// Full names for tooltips (shown on hover)
export const filtersTooltipTranslations: Record<string, Translation> = {
  lotItemNumbers: {
    es: 'Número de Artículo de Lote',
    en: 'Lot Item Number',
  },
  itemCodes: {
    es: 'Código de Artículo',
    en: 'Item Code',
  },
  providerReferences: {
    es: 'Proveedor',
    en: 'Provider',
  },
  manufacturerReferences: {
    es: 'Fabricante',
    en: 'Manufacturer',
  },
  manufacturerLotNumbers: {
    es: 'Número de Lote del Fabricante',
    en: 'Manufacturer Lot Number',
  },
  manufacturerLabelNumbers: {
    es: 'Número de Etiqueta del Fabricante',
    en: 'Manufacturer Label Number',
  },
  invoiceReferences: {
    es: 'Factura',
    en: 'Invoice',
  },
  deliveryNoteReferences: {
    es: 'Nota de Entrega',
    en: 'Delivery Note',
  },
  locations: {
    es: 'Ubicaciones',
    en: 'Locations',
  },
  locationsTooltip: {
    es: 'Seleccione una o más ubicaciones del árbol jerárquico',
    en: 'Select one or more locations from the hierarchical tree',
  },
};

export type InventoryFilters =
  | 'locations'
  | 'lotItemNumbers'
  | 'itemCodes'
  | 'providerReferences'
  | 'manufacturerReferences'
  | 'manufacturerLotNumbers'
  | 'manufacturerLabelNumbers'
  | 'invoiceReferences'
  | 'deliveryNoteReferences'
  | 'fromDateReceived'
  | 'toDateReceived';

/**
 * Get filters available for Lot Item view
 */
export function getLotItemFilters(translateService: TranslateService): FilterField[] {
  const currentLang = (translateService.currentLang || 'en') as keyof Translation;

  return [
    { name: 'lotItemNumbers', label: filtersTranslations['lotItemNumbers'][currentLang], type: 'autocomplete' },
    { name: 'itemCodes', label: filtersTranslations['itemCodes'][currentLang], type: 'autocomplete' },
    { name: 'providerReferences', label: filtersTranslations['providerReferences'][currentLang], type: 'autocomplete' },
    { name: 'manufacturerReferences', label: filtersTranslations['manufacturerReferences'][currentLang], type: 'autocomplete' },
    { name: 'manufacturerLotNumbers', label: filtersTranslations['manufacturerLotNumbers'][currentLang], type: 'autocomplete' },
    { name: 'manufacturerLabelNumbers', label: filtersTranslations['manufacturerLabelNumbers'][currentLang], type: 'autocomplete' },
    { name: 'invoiceReferences', label: filtersTranslations['invoiceReferences'][currentLang], type: 'autocomplete' },
    { name: 'deliveryNoteReferences', label: filtersTranslations['deliveryNoteReferences'][currentLang], type: 'autocomplete' },
  ];
}

/**
 * Get filters available for Inventory Summary view
 */
export function getInventorySummaryFilters(translateService: TranslateService): FilterField[] {
  const currentLang = (translateService.currentLang || 'en') as keyof Translation;

  return [{ name: 'itemCodes', label: filtersTranslations['itemCodes'][currentLang], type: 'autocomplete' }];
}

/**
 * Get filters based on current view mode
 */
export function getFiltersForViewMode(viewMode: InventoryViewMode, translateService: TranslateService): FilterField[] {
  if (viewMode === 'lotItem') {
    return getLotItemFilters(translateService);
  }
  return getInventorySummaryFilters(translateService);
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use getFiltersForViewMode instead
 */
export function getCustomFilters(translateService: TranslateService): FilterField[] {
  return getLotItemFilters(translateService);
}
